/**
 * The menu and its pages (decision 0013): your expeditions, how to play, settings with its page
 * of languages (decision 0026), get the iOS app (web only), more from Great Scott!, about. Full
 * screen on a phone, a drawer from the left on desktop, one shared content. Pages are drawn from
 * a snapshot of the game's data each time one opens.
 */
import { LANGUAGES, type Locale, THEMES } from "@mistsweeper/assets";
import colortapIcon from "@mistsweeper/assets/apps/colortap.png?url";
import kronoIcon from "@mistsweeper/assets/apps/krono.png?url";
import ordnaIcon from "@mistsweeper/assets/apps/ordna.png?url";
import tapotronIcon from "@mistsweeper/assets/apps/tapotron.png?url";
import { TESTFLIGHT_URL } from "../features.ts";
import type { StringKey } from "../i18n.ts";
import { BETA_PARTS, betaBand } from "./beta.ts";
import { esc, icon, logo, SQUARE } from "./dom.ts";
import testflightQr from "./testflight-qr.svg?raw";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

export type MenuPage =
  | "home"
  | "expeditions"
  | "list"
  | "howto"
  | "settings"
  | "language"
  | "getapp"
  | "apps"
  | "about"
  | "privacy"
  | "beta";

export interface ArchiveDay {
  readonly n: number;
  readonly dateShort: string;
  readonly state: "progress" | "today" | "best" | "sealed" | "notPlayed" | "played";
  /** The distance, formatted, or null when not played. */
  readonly distance: string | null;
  /** The share map's rows, or null when not played. */
  readonly map: readonly string[] | null;
}

/** A row of today's expeditions (decision 0039), its words ready. */
export interface ListPageRow {
  /** The expedition's id on the list, so its name can be reported; null for one not on it. */
  readonly id: number | null;
  /**
   * The place in digits, `41`, with no sign, since some languages read `#41` as an expedition's
   * number; empty for the player's own expedition while its place is not known.
   */
  readonly place: string;
  /**
   * The name, or null while it is hidden after reports; for the player's own expedition, null
   * before a name has been chosen.
   */
  readonly name: string | null;
  readonly distance: string;
  /** `143 tiles`: the ground explored, which ranks expeditions that went as far. */
  readonly tiles: string;
  readonly map: readonly string[];
  /** The player's own expedition: marked with the banner. */
  readonly own: boolean;
}

/** The page of today's expeditions, as last fetched. */
export interface ListPage {
  readonly state: "loading" | "ready" | "unreachable";
  /** `Expedition #22, Thu 29 Oct`. */
  readonly kicker: string;
  /** `359 expeditions so far`, or null before the list has come. */
  readonly count: string | null;
  readonly rows: readonly ListPageRow[];
  /**
   * The player's own expedition when it is not among the rows: pinned at the foot, with the Add
   * button while it is not on the list.
   */
  readonly own: (ListPageRow & { readonly added: boolean }) | null;
}

export interface MenuData {
  readonly web: boolean;
  /** Whether Get the iOS app shows (features.ts). */
  readonly getApp: boolean;
  readonly desk: boolean;
  readonly debug: boolean;
  readonly streak: string;
  readonly streakDays: number;
  readonly bestDaily: string | null;
  readonly expeditionsPlayed: number;
  readonly history: readonly ArchiveDay[];
  readonly endless: {
    readonly best: string | null;
    readonly last: string | null;
    readonly runs: number;
  };
  readonly theme: "day" | "night";
  /** Whether the theme follows the system's light or dark mode, none being chosen (decision 0036). */
  readonly themeAuto: boolean;
  /** The language the game speaks. */
  readonly locale: Locale;
  /** The language the player chose, or null when the game follows the browser's. */
  readonly localeChosen: Locale | null;
  /** The language the browser's would give. */
  readonly systemLocale: Locale;
  readonly reduceMotion: boolean;
  readonly music: boolean;
  readonly sounds: boolean;
  readonly frameTimes: boolean;
  /** Tilt-shift, a developer trial: from 0, off, to 1. */
  readonly tilt: number;
  /** The banner for the visit (decision 0035), `1` for the game's own, among those the models have. */
  readonly banner: string;
  readonly banners: readonly string[];
  readonly version: string;
  /** Where questions and feedback go. */
  readonly email: string;
  /** Today's expeditions, or null where `?list=0` leaves the list out. */
  readonly list: ListPage | null;
  /** The list's part of Settings: the name, or null before the first expedition added. */
  readonly listSettings: { readonly name: string | null; readonly auto: boolean } | null;
}

const TITLES: Record<Exclude<MenuPage, "home">, StringKey> = {
  expeditions: "menu.expeditions",
  list: "menu.list",
  howto: "menu.howto",
  settings: "settings.title",
  language: "settings.language",
  getapp: "menu.getApp",
  apps: "menu.apps",
  about: "menu.about",
  privacy: "privacy.title",
  beta: "beta.title",
};

/**
 * The close button draws the three lines of menu.svg, in the same place as the menu button, and
 * turns them into a cross as the menu opens (the style sheet moves the lines).
 */
const BURGER =
  '<svg class="burger" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/></svg>';

export function menuShell(t: T, beta: boolean): string {
  return `
  <div class="menu" hidden>
    <button type="button" class="menu-scrim" data-action="closeMenu" tabindex="-1" aria-label="${esc(t("menu.close"))}"></button>
    <div class="menu-panel" role="dialog" aria-modal="true" aria-labelledby="menuTitle">
      <header class="menu-head">
        <button type="button" class="head-button close" data-action="closeMenu" aria-label="${esc(t("menu.close"))}">${BURGER}</button>
        <button type="button" class="head-button back" data-action="menuBack" aria-label="${esc(t("menu.back"))}">${icon("back")}</button>
        <div class="menu-title">${SQUARE}<h2 id="menuTitle" data-bind="menuTitle"></h2></div>
        ${beta ? betaBand(t, "page:beta") : ""}
      </header>
      <div class="menu-body" data-ref="menuBody"></div>
    </div>
  </div>`;
}

export function pageTitle(t: T, page: MenuPage): string {
  return page === "home" ? t("menu.close") : t(TITLES[page]);
}

const row = (action: string, title: string, line: string | null) => `
  <button type="button" class="menu-row" data-action="${action}">
    <span class="menu-row-text"><span class="menu-item">${esc(title)}</span>${line === null ? "" : `<span class="kicker">${esc(line)}</span>`}</span>
    ${icon("chevronRight", 22)}
  </button>`;

/** A plain row at the foot of the menu: a page of its own, or a way out of the game. */
const outRow = (action: string, label: string, out: boolean) => `
  <button type="button" class="out-row" data-action="${action}">${esc(label)}${icon(out ? "external" : "chevronRight", 18)}</button>`;

const section = (label: string, rule = true) =>
  `<div class="section-label kicker${rule ? " ruled" : ""}">${esc(label)}</div>`;

function home(t: T, d: MenuData): string {
  return `
    <div class="menu-brand">${logo("mist", t("app.title"))}<div class="kicker tagline">${esc(t("app.tagline"))}</div></div>
    <nav class="menu-rows" aria-label="${esc(t("hud.menu"))}">
      ${d.list ? row("page:list", t("menu.list"), t("menu.listLine")) : ""}
      ${row("page:expeditions", t("menu.expeditions"), t("menu.expeditionsLine", { streak: d.streak, distance: d.bestDaily ?? "0 m" }))}
      ${row("page:howto", t("menu.howto"), t("menu.howtoLine"))}
      ${row("page:settings", t("settings.title"), t("menu.settingsLine", { theme: t(d.theme === "night" ? "theme.night" : "theme.day"), language: t("lang.name") }))}
      ${d.getApp ? row("page:getapp", t("menu.getApp"), t("menu.getAppLine")) : ""}
      ${row("page:apps", t("menu.apps"), t("menu.appsLine"))}
      ${row("page:about", t("menu.about"), null)}
    </nav>
    <div class="out-rows">
      ${outRow("page:privacy", t("menu.privacy"), false)}
      ${outRow("feedback", t("menu.feedback"), true)}
    </div>
    <div class="kicker menu-version">${esc(t("menu.version", { version: d.version }))}</div>`;
}

/**
 * A day's map from its share map rows, in ink and paper: a row of Your expeditions, and the
 * furthest point of the result sheet.
 */
export function miniMap(map: readonly string[] | null): string {
  if (map === null) return '<span class="mini-map empty" aria-hidden="true"></span>';
  const rows = map.map((r) => Array.from(r));
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const cell = (c: string) =>
    c === "⬜" ? "open" : c === "\u{1F3E0}" ? "cabin" : c === "\u{1F7E5}" ? "end" : "mist";
  const cells = rows.flatMap((r) => r.map((c) => `<i class="${cell(c)}"></i>`)).join("");
  return `<span class="mini-map" aria-hidden="true" style="--cols:${cols};--rows:${rows.length}">${cells}</span>`;
}

function expeditions(t: T, d: MenuData): string {
  const kicker = (day: ArchiveDay) => {
    const extra: Record<ArchiveDay["state"], StringKey | null> = {
      progress: "archive.today",
      today: "archive.todayWord",
      best: "archive.best",
      sealed: "archive.sealed",
      notPlayed: "archive.notPlayed",
      played: null,
    };
    const key = extra[day.state];
    return key === null ? day.dateShort : `${day.dateShort}${t("punct.comma")}${t(key)}`;
  };
  const days = d.history
    .map(
      (day) => `
    <div class="archive-row">
      ${miniMap(day.map)}
      <div class="archive-text"><span class="archive-title">${esc(t("mode.daily", { n: day.n }))}</span><span class="kicker">${esc(kicker(day))}</span></div>
      ${day.distance === null ? "" : `<span class="stat-value">${esc(day.distance)}</span>`}
    </div>`,
    )
    .join("");
  const stat = (value: string, label: string) =>
    `<div class="big-stat"><span class="big-figure">${esc(value)}</span><span class="kicker">${esc(label)}</span></div>`;
  const small = (value: string, label: string) =>
    `<div class="small-stat"><span class="stat-value">${esc(value)}</span><span class="kicker">${esc(label)}</span></div>`;
  return `
    <div class="stat-grid three ruled-bottom">
      ${stat(String(d.streakDays), t("archive.dayStreak"))}
      ${stat(d.bestDaily ?? "0 m", t("over.yourBest"))}
      ${stat(String(d.expeditionsPlayed), t("archive.expeditions"))}
    </div>
    ${section(t("archive.daily"))}
    ${days}
    ${section(t("button.endless"))}
    <div class="stat-grid three ruled-bottom">
      ${small(d.endless.best ?? "0 m", t("archive.endlessBest"))}
      ${small(d.endless.last ?? "0 m", t("archive.endlessLast"))}
      ${small(String(d.endless.runs), t("archive.endlessRuns"))}
    </div>
    ${d.web ? `<p class="page-note">${esc(t(d.getApp ? "archive.webNote" : "archive.webNoteShort"))}</p>` : ""}`;
}

/**
 * Today's top explorers (decisions 0039 and 0040): the first rows of the day's list, the furthest
 * first and, as far, the most tiles first, each with its place, its map, its tiles and its
 * distance; the player's own marked with the banner, and pinned at the foot when it is not among
 * them, with Add your name under it while it is not on the list. Holding a row, or a right click,
 * reports its name.
 */
function list(t: T, d: MenuData): string {
  const page = d.list;
  if (!page) return "";
  const line = (r: ListPageRow, kicker: string | null) => `
    <div class="archive-row list-row${r.own ? " own" : ""}"${r.id !== null && !r.own && r.name !== null ? ` data-report="${r.id}"` : ""}${r.own ? ` aria-label="${esc(t("list.yours"))}"` : ""}>
      <span class="list-place">${esc(r.place)}</span>
      ${miniMap(r.map)}
      <div class="archive-text"><span class="archive-title${r.name === null ? "" : " player-name"}">${esc(r.name ?? t(r.own ? "list.you" : "list.hiddenName"))}</span>${kicker === null ? "" : `<span class="kicker">${esc(kicker)}</span>`}</div>
      ${r.own ? icon("banner", 30) : ""}
      <span class="stat-value">${esc(r.distance)}</span>
    </div>`;
  const head = `
    <div class="list-head">
      <div class="kicker">${esc(page.kicker)}</div>
      ${page.count === null ? "" : `<div class="list-count">${esc(page.count)}</div>`}
    </div>`;
  if (page.state !== "ready" && page.rows.length === 0) {
    return `${head}<p class="page-note">${esc(t(page.state === "loading" ? "list.loading" : "list.unreachable"))}</p>`;
  }
  // The player's own row says its tiles as every row does; the banner marks it (Daniel, 2026-10-29).
  const rows = page.rows.map((r) => line(r, r.tiles)).join("");
  const own = page.own;
  const foot = own
    ? `<div class="list-foot">${line(own, own.added ? own.tiles : t("list.notAdded"))}${
        own.added
          ? ""
          : `<div class="list-add"><button type="button" class="primary" data-action="listAdd">${esc(t("list.addTitle"))}</button></div>`
      }</div>`
    : "";
  const empty = page.rows.length === 0 ? `<p class="page-intro">${esc(t("list.empty"))}</p>` : "";
  return `${head}${empty}<div class="list-rows">${rows}</div>${foot}`;
}

function howto(t: T, d: MenuData): string {
  const step = (n: number, body: string) => `
    <li class="step"><span class="step-number" aria-hidden="true">${n}</span><div class="step-body">${body}</div></li>`;
  const p = (key: StringKey) => `<p>${esc(t(key))}</p>`;
  const tile = (cls: string, inner = "") => `<span class="ex-tile ${cls}">${inner}</span>`;
  const example = `
    <div class="example" aria-hidden="true">
      ${tile("mist")}${tile("mist", icon("banner", 30))}${tile("mist")}
      ${tile("open")}${tile("open", '<span class="digit">2</span>')}${tile("open")}
      ${tile("open", '<span class="digit">1</span>')}${tile("open")}${tile("open")}
    </div>`;
  const controls: [StringKey, StringKey][] = d.desk
    ? [
        ["controls.click", "controls.reveal"],
        ["controls.rightClick", "controls.banner"],
        ["controls.drag", "controls.pan"],
        ["controls.rightDrag", "controls.turnTilt"],
        ["controls.scroll", "controls.zoom"],
        ["controls.qe", "controls.turn"],
        ["controls.tg", "controls.tilt"],
      ]
    : [
        ["controls.drag", "controls.pan"],
        ["controls.pinch", "controls.zoom"],
        ["controls.twist", "controls.turn"],
        ["controls.twoFingers", "controls.tilt"],
      ];
  return `
    <button type="button" class="primary small howto-try" data-action="lesson">${esc(t("howto.tryLesson"))}</button>
    <ol class="steps">
      ${step(1, p("howto.1"))}
      ${step(2, p("howto.2"))}
      ${step(3, `<p>${esc(t("punct.join", { first: t("howto.3"), next: t("howto.3example") }))}</p>${example}`)}
      ${step(4, p(d.desk ? "howto.4mouse" : "howto.4touch"))}
      ${step(5, p("howto.5"))}
      ${step(6, p("howto.6"))}
    </ol>
    ${section(t("controls.title"))}
    <dl class="controls">${controls.map(([a, b]) => `<div class="control"><dt>${esc(t(a))}</dt><dd>${esc(t(b))}</dd></div>`).join("")}</dl>`;
}

function segmented(
  label: string,
  options: { action: string; text: string; on: boolean; swatch?: string }[],
): string {
  return `
    <div class="segmented" role="group" aria-label="${esc(label)}">
      ${options
        .map(
          (o) =>
            `<button type="button" data-action="${o.action}" aria-pressed="${o.on}">${o.swatch ? `<span class="swatch" style="background:${o.swatch}" aria-hidden="true"></span>` : ""}${esc(o.text)}</button>`,
        )
        .join("")}
    </div>`;
}

function toggle(action: string, title: string, line: string, on: boolean): string {
  return `
    <button type="button" class="switch-row" role="switch" aria-checked="${on}" data-action="${action}">
      <span class="switch-text"><span class="switch-title">${esc(title)}</span><span class="switch-line">${esc(line)}</span></span>
      <span class="switch" aria-hidden="true"><span></span></span>
    </button>`;
}

function settings(t: T, d: MenuData): string {
  return `
    ${section(t("settings.theme"), false)}
    <div class="padded">${segmented(t("settings.theme"), [
      {
        action: "theme:day",
        text: t("theme.day"),
        on: d.theme === "day",
        swatch: THEMES.day.paper,
      },
      {
        action: "theme:night",
        text: t("theme.night"),
        on: d.theme === "night",
        swatch: THEMES.night.paper,
      },
    ])}</div>
    ${toggle("themeAuto", t("settings.themeAuto"), t("settings.themeAutoLine"), d.themeAuto)}
    ${section(t("settings.language"), false)}
    <div class="menu-rows">${row("page:language", t("lang.name"), d.localeChosen === null ? t("settings.languageAuto") : null)}</div>
    ${section(t("settings.feel"))}
    ${toggle("motion", t("settings.motion"), t("settings.motionLine"), d.reduceMotion)}
    ${toggle("music", t("settings.music"), t("settings.musicLine"), d.music)}
    ${toggle("sounds", t("settings.sounds"), t("settings.soundsLine"), d.sounds)}
    ${d.listSettings ? listSettings(t, d.listSettings) : ""}
    ${
      d.getApp
        ? `<div class="promo"><p>${esc(t("settings.appPromo"))}</p><button type="button" class="secondary" data-action="page:getapp">${esc(t("menu.getApp"))}</button></div>`
        : ""
    }
    ${
      d.debug
        ? `${section(t("settings.developer"))}
           ${toggle("frameTimes", t("settings.debug"), "", d.frameTimes)}
           <div class="padded zoom-row"><span class="switch-title">${esc(t("settings.zoom"))}</span>
             <button type="button" class="secondary" data-action="zoomOut">${esc(t("button.zoomOut"))}</button>
             <button type="button" class="secondary" data-action="zoomIn">${esc(t("button.zoomIn"))}</button></div>
           <label class="padded tilt-row"><span class="switch-title">${esc(t("settings.tilt"))}</span>
             <input type="range" min="0" max="100" step="1" value="${Math.round(d.tilt * 100)}" data-input="tilt"></label>
           ${
             d.banners.length > 1
               ? `<label class="padded tilt-row"><span class="switch-title">${esc(t("button.banner"))}</span>
             <select data-input="banner">${d.banners
               .map(
                 (b) =>
                   `<option value="${esc(b)}"${b === d.banner ? " selected" : ""}>${esc(b)}</option>`,
               )
               .join("")}</select></label>`
               : ""
           }`
        : ""
    }`;
}

/** Today's expeditions in Settings: adding by itself, the name and taking them off the lists. */
function listSettings(t: T, s: NonNullable<MenuData["listSettings"]>): string {
  if (s.name === null) {
    return `${section(t("menu.list"))}<p class="page-note">${esc(t("settings.listNote"))}</p>`;
  }
  return `
    ${section(t("menu.list"))}
    ${toggle("listAuto", t("settings.listAuto"), t("settings.listAutoLine"), s.auto)}
    <div class="menu-rows">
      <button type="button" class="menu-row" data-action="listRename" data-name="${esc(s.name)}">
        <span class="menu-row-text"><span class="menu-item">${esc(t("settings.listName"))}</span><span class="kicker player-name">${esc(s.name)}</span></span>
        ${icon("chevronRight", 22)}
      </button>
    </div>
    <div class="out-rows">${outRow("listRemove", t("settings.listRemove"), false)}</div>`;
}

/**
 * The languages (decision 0026): following the browser's first, then each language by its own
 * name in its own script, with its word for mist under it, the one chosen marked with the banner.
 * Drafts show only with the developer settings.
 */
function language(t: T, d: MenuData): string {
  const system = LANGUAGES.find((l) => l.code === d.systemLocale);
  const choice = (action: string, title: string, line: string, on: boolean, lang?: string) => `
    <button type="button" class="menu-row language-row" role="radio" aria-checked="${on}" data-action="${action}">
      <span class="menu-row-text"${lang ? ` lang="${lang}"` : ""}><span class="menu-item">${esc(title)}</span><span class="kicker">${esc(line)}</span></span>
      ${on ? icon("banner", 30) : ""}
    </button>`;
  const languages = LANGUAGES.filter((l) => l.released || d.debug).map((l) =>
    choice(
      `locale:${l.code}`,
      l.name,
      l.released ? l.mist : `${l.mist}${t("punct.comma")}${t("settings.languageDraft")}`,
      d.localeChosen === l.code,
      l.code,
    ),
  );
  return `
    <div class="menu-rows" role="radiogroup" aria-label="${esc(t("settings.language"))}">
      ${choice("locale:auto", t("settings.languageAuto"), t("settings.languageAutoLine", { language: system?.name ?? "English" }), d.localeChosen === null)}
      ${languages.join("")}
    </div>`;
}

function getApp(t: T, d: MenuData): string {
  // The widget leads since build 8 brought it to TestFlight (decision 0027).
  const bullets: StringKey[] = [
    "getApp.widget",
    "getApp.reminder",
    "getApp.haptics",
    "getApp.offline",
  ];
  // The code is drawn in Day's ink on Day's paper in either theme, since not every camera reads
  // one with its colours turned round.
  const day = THEMES.day;
  const qr = testflightQr.replace(
    "<svg ",
    `<svg class="qr-code" role="img" aria-label="${esc(t("getApp.qrLabel"))}" `,
  );
  return `
    <div class="page-pad">
      <p class="lead">${esc(t("getApp.intro"))}</p>
      <ul class="bullets">${bullets.map((k) => `<li><span class="bullet" aria-hidden="true"></span>${esc(t(k))}</li>`).join("")}</ul>
      <p class="page-text flush">${esc(t("getApp.beta"))}</p>
      ${d.desk ? `<div class="qr-row"><div class="qr" style="color: ${day.ink}; background: ${day.paper}">${qr}</div><p>${esc(t("getApp.qr"))}</p></div>` : ""}
      <button type="button" class="primary" data-action="open" data-url="${esc(TESTFLIGHT_URL)}">${esc(t("getApp.join"))}</button>
      <p class="page-note flush">${esc(t("getApp.note"))}</p>
    </div>`;
}

/** The other games and apps from Great Scott!, newest first, each linked to the App Store. */
const APPS: readonly { name: string; line: StringKey; icon: string; appStore: string }[] = [
  { name: "Ordna", line: "apps.ordna", icon: ordnaIcon, appStore: "6763764205" },
  { name: "Krono", line: "apps.krono", icon: kronoIcon, appStore: "1613353323" },
  { name: "Tapotron", line: "apps.tapotron", icon: tapotronIcon, appStore: "6738282470" },
  { name: "Color Tap", line: "apps.colortap", icon: colortapIcon, appStore: "1516150031" },
];

function apps(t: T): string {
  const app = (a: (typeof APPS)[number]) => `
    <div class="app-row">
      <img class="app-icon" src="${a.icon}" alt="" width="60" height="60" draggable="false">
      <div class="app-text"><span class="app-name">${esc(a.name)}</span><span class="app-line">${esc(t(a.line))}</span></div>
      <button type="button" class="secondary small" data-action="open" data-url="https://apps.apple.com/app/id${a.appStore}" aria-label="${esc(`${a.name}, ${t("apps.appStore")}`)}">${esc(t("apps.appStore"))}</button>
    </div>`;
  return `
    <p class="page-intro">${esc(t("apps.intro"))}</p>
    ${APPS.map(app).join("")}`;
}

/**
 * What the game keeps and what it does not (Daniel, 2026-10-17). It describes the web build as it
 * is: local storage only, no cookies, visits counted by Plausible on the production site, served
 * by Cloudflare. Keep it true when that changes.
 */
function privacy(t: T, d: MenuData): string {
  const part = (title: StringKey, body: StringKey) =>
    `${section(t(title), false)}<p class="page-text">${esc(t(body))}</p>`;
  return `
    <p class="page-intro">${esc(t("privacy.intro"))}</p>
    ${part("privacy.storedTitle", "privacy.stored")}
    ${part("menu.list", "privacy.list")}
    ${part("privacy.listRemoveTitle", "privacy.listRemove")}
    ${part("privacy.noneTitle", "privacy.none")}
    ${part("privacy.visitsTitle", "privacy.visits")}
    ${part("privacy.hostTitle", "privacy.host")}
    ${part("privacy.shareTitle", "privacy.share")}
    ${part("privacy.contactTitle", "privacy.contact")}
    <p class="page-text"><a class="text-link" href="mailto:${esc(d.email)}">${esc(d.email)}</a></p>
    <div class="kicker page-foot">${esc(t("privacy.updated"))}</div>`;
}

/** About the beta (decision 0044), kept true with the page at /beta in beta.ts. */
function beta(t: T, d: MenuData): string {
  const part = (title: StringKey, body: StringKey) =>
    `${section(t(title), false)}<p class="page-text">${esc(t(body))}</p>`;
  return `
    <p class="page-intro">${esc(t("beta.intro"))}</p>
    ${BETA_PARTS.map(([title, body]) => part(title, body)).join("")}
    <div class="page-pad">
      <button type="button" class="primary" data-action="feedback">${esc(t("menu.feedback"))}</button>
    </div>
    ${part("beta.changesTitle", "beta.changes")}
    ${
      d.getApp
        ? `${part("menu.getApp", "getApp.beta")}
    <div class="page-pad">
      <button type="button" class="primary" data-action="open" data-url="${esc(TESTFLIGHT_URL)}">${esc(t("getApp.join"))}</button>
    </div>`
        : ""
    }`;
}

function about(t: T, d: MenuData): string {
  return `
    <div class="menu-brand">${logo("mist", t("app.title"))}<div class="kicker tagline">${esc(t("app.tagline"))}</div></div>
    <div class="page-pad prose">
      <p>${esc(t("about.body1"))}</p>
      <p>${esc(t("about.body2"))}</p>
      <p>${esc(t("about.body3"))}</p>
      <p>${esc(t("about.made"))}</p>
      <div class="kicker">${esc(t("about.version", { version: d.version }))}</div>
    </div>`;
}

export function pageHtml(t: T, page: MenuPage, d: MenuData): string {
  switch (page) {
    case "home":
      return home(t, d);
    case "expeditions":
      return expeditions(t, d);
    case "list":
      return list(t, d);
    case "howto":
      return howto(t, d);
    case "settings":
      return settings(t, d);
    case "language":
      return language(t, d);
    case "getapp":
      return getApp(t, d);
    case "apps":
      return apps(t);
    case "privacy":
      return privacy(t, d);
    case "beta":
      return beta(t, d);
    case "about":
      return about(t, d);
  }
}
